import { Typography } from '@euler/app/components/typography';
import { ImportQuestion } from '@euler/app/flows/apdm/imports/components/import-question';
import { importStageLabel } from '@euler/app/flows/apdm/imports/functions/importStageLabel';
import { BottomPanel } from '@euler/components/BottomPanel';
import {
  ApdmImportStage,
  ApdmImportState,
} from '@euler/functions/apdm/import/importState';
import {
  ApdmImportAction,
  ApdmImportController,
} from '@euler/functions/apdm/import/useApdmImport';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Check, X } from 'lucide-react-native';
import { useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';
import { CardNotice, ChatCardBadge } from '../components/ChatCard';
import { PrimaryButton, SecondaryButton } from '../components/PrimaryButton';
import { aiColors } from '../theme';
import { ImportDetailSummary } from './ImportDetailSummary';

const kStages = [
  ApdmImportStage.Reading,
  ApdmImportStage.Recognizing,
  ApdmImportStage.Organizing,
];

const kBadges: Record<ApdmImportState['kind'], [string, string]> = {
  working: ['读取中', aiColors.primary],
  question: ['待确认', aiColors.amber],
  ready: ['已整理好', aiColors.green],
  publishing: ['导入中', aiColors.primary],
  published: ['已导入', aiColors.green],
  failed: ['未完成', aiColors.destructive],
  deleted: ['已删除', aiColors.mutedForeground],
};

function statusLine(state: ApdmImportState): string | null {
  switch (state.kind) {
    case 'working':
      return '正在整理这份文件，通常不到一分钟，可以先去做别的事。';
    case 'question':
      return '没能自动找到下面的内容，请指出它在哪一列。';
    case 'ready':
      return '已整理好，确认无误后开始导入。';
    case 'publishing':
      return '正在导入，完成前客户会陆续出现在营销名单里。';
    case 'published':
      return '已导入，这些客户已经在营销名单里了。';
    case 'failed':
      return state.restartable
        ? '导入中途停止了，可以重新开始。'
        : '这份文件没能导入，请删除后换一份文件再试。';
    case 'deleted':
      return '这次导入已删除。';
  }
}

/**
 * One import, in place over the conversation: where it stands, what it
 * brings in, and what to do with it. Opened from the chat's import card,
 * which shares its controller, so both always show the same state.
 */
export function ImportDetailPanel({
  visible,
  controller,
  onClose,
}: {
  visible: boolean;
  controller: ApdmImportController;
  onClose: () => void;
}) {
  const { rpx } = useResponsiveContext();
  const { state, pending, failure, actions } = controller;
  const [confirming, setConfirming] = useState(false);
  if (!state) return null;
  const summary =
    state.kind === 'ready' ||
    state.kind === 'publishing' ||
    state.kind === 'published' ||
    state.kind === 'failed'
      ? state.summary
      : null;
  const [badge, badgeColor] = kBadges[state.kind];
  const close = () => {
    setConfirming(false);
    onClose();
  };
  return (
    <BottomPanel visible={visible} onClose={close} tall>
      <View style={{ flex: 1 }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: rpx(12),
            paddingHorizontal: rpx(20),
            paddingTop: rpx(20),
            paddingBottom: rpx(14),
            borderBottomWidth: 1,
            borderBottomColor: aiColors.border,
          }}
        >
          <View style={{ flex: 1, gap: rpx(4) }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(8),
              }}
            >
              <Typography.Text bold size={rpx(18)} color={aiColors.foreground}>
                客户表格
              </Typography.Text>
              <ChatCardBadge label={badge} color={badgeColor} />
            </View>
            <Typography.Text
              size={rpx(12)}
              color={aiColors.mutedForeground}
              numberOfLines={1}
              ellipsizeMode="middle"
            >
              {state.fileName}
            </Typography.Text>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(6),
                marginTop: rpx(6),
              }}
            >
              {state.kind === 'publishing' ? (
                <ActivityIndicator size="small" color={aiColors.primary} />
              ) : null}
              <Typography.Text
                size={rpx(13)}
                color={
                  state.kind === 'failed'
                    ? aiColors.destructive
                    : aiColors.foreground
                }
                style={{ flex: 1 }}
              >
                {statusLine(state)}
              </Typography.Text>
            </View>
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="关闭"
            hitSlop={8}
            onPress={close}
            style={{
              width: rpx(32),
              height: rpx(32),
              borderRadius: rpx(16),
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: aiColors.muted,
            }}
          >
            <X size={16} color={aiColors.mutedForeground} strokeWidth={2.2} />
          </Pressable>
        </View>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ padding: rpx(20), gap: rpx(16) }}
        >
          {state.kind === 'working' ? <Stages stage={state.stage} /> : null}
          {state.kind === 'question' ? (
            <ImportQuestion
              key={state.id}
              question={state.question}
              busy={pending === ApdmImportAction.Answer}
              onAnswer={answers => void actions.answer(answers)}
            />
          ) : null}
          {summary ? <ImportDetailSummary summary={summary} /> : null}
          {failure ? <CardNotice tone="danger" text={failure} /> : null}
        </ScrollView>
        <Footer
          state={state}
          pending={pending}
          confirming={confirming}
          onConfirming={setConfirming}
          onStart={() => void actions.start()}
          onDelete={() => {
            void (async () => {
              if (await actions.remove()) close();
            })();
          }}
        />
      </View>
    </BottomPanel>
  );
}

/** The system works through the file by itself; nothing to do but wait. */
function Stages({ stage }: { stage: ApdmImportStage }) {
  const { rpx } = useResponsiveContext();
  const current = kStages.indexOf(stage);
  return (
    <View accessibilityLiveRegion="polite" style={{ gap: rpx(12) }}>
      {kStages.map((entry, index) => (
        <View
          key={entry}
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(10) }}
        >
          <View
            style={{
              width: rpx(22),
              height: rpx(22),
              borderRadius: rpx(11),
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor:
                index < current ? aiColors.primary : aiColors.muted,
            }}
          >
            {index < current ? (
              <Check
                size={12}
                color={aiColors.primaryForeground}
                strokeWidth={3}
              />
            ) : index === current ? (
              <ActivityIndicator size="small" color={aiColors.primary} />
            ) : null}
          </View>
          <Typography.Text
            medium={index === current || undefined}
            size={rpx(14)}
            color={
              index > current ? aiColors.mutedForeground : aiColors.foreground
            }
          >
            {importStageLabel(entry)}
          </Typography.Text>
        </View>
      ))}
    </View>
  );
}

/**
 * Start the import, or take it back out. Deleting asks in place: a confirm
 * screen would open behind the panel.
 */
function Footer({
  state,
  pending,
  confirming,
  onConfirming,
  onStart,
  onDelete,
}: {
  state: ApdmImportState;
  pending: ApdmImportAction | null;
  confirming: boolean;
  onConfirming: (confirming: boolean) => void;
  onStart: () => void;
  onDelete: () => void;
}) {
  const { rpx } = useResponsiveContext();
  const startable =
    state.kind === 'ready' || (state.kind === 'failed' && state.restartable);
  const deletable =
    state.kind !== 'publishing' &&
    state.kind !== 'deleted' &&
    state.kind !== 'working';
  if (!startable && !deletable) return null;
  return (
    <View
      style={{
        gap: rpx(10),
        paddingHorizontal: rpx(20),
        paddingTop: rpx(12),
        paddingBottom: rpx(16),
        borderTopWidth: 1,
        borderTopColor: aiColors.border,
      }}
    >
      {confirming ? (
        <>
          <View style={{ gap: rpx(2) }}>
            <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
              删除这次导入？
            </Typography.Text>
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              {state.kind === 'published'
                ? '这份文件导入的来店记录和保养到期都会撤回。'
                : '这份文件不会再导入。'}
            </Typography.Text>
          </View>
          <View style={{ flexDirection: 'row', gap: rpx(10) }}>
            <SecondaryButton
              title="取消"
              disabled={pending === ApdmImportAction.Delete}
              onPress={() => onConfirming(false)}
            />
            <Pressable
              accessibilityRole="button"
              disabled={pending === ApdmImportAction.Delete}
              onPress={onDelete}
              style={({ pressed }) => ({
                flexGrow: 1,
                height: rpx(40),
                borderRadius: rpx(11),
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: aiColors.destructive,
                opacity: pressed ? 0.8 : 1,
              })}
            >
              {pending === ApdmImportAction.Delete ? (
                <ActivityIndicator
                  size="small"
                  color={aiColors.primaryForeground}
                />
              ) : (
                <Typography.Text
                  medium
                  size={rpx(13)}
                  color={aiColors.primaryForeground}
                >
                  删除
                </Typography.Text>
              )}
            </Pressable>
          </View>
        </>
      ) : (
        <>
          {startable ? (
            <PrimaryButton
              title={state.kind === 'ready' ? '开始导入' : '重新开始导入'}
              loading={pending === ApdmImportAction.Start}
              disabled={pending === ApdmImportAction.Delete}
              onPress={onStart}
            />
          ) : null}
          {deletable ? (
            <Pressable
              accessibilityRole="button"
              disabled={pending === ApdmImportAction.Start}
              hitSlop={6}
              onPress={() => onConfirming(true)}
              style={{ alignSelf: 'center', paddingVertical: rpx(4) }}
            >
              <Typography.Text size={rpx(13)} color={aiColors.destructive}>
                删除这次导入
              </Typography.Text>
            </Pressable>
          ) : null}
        </>
      )}
    </View>
  );
}
